/**
 * UI color constants for the application
 * Contains color definitions for UI components, gradients, and theme variants
 */

// Gradient colors for UI components - 8 distinct colors
export const UI_GRADIENT_COLORS = [
  'from-blue-100/50 to-blue-50/50', // Blue
  'from-purple-100/50 to-purple-50/50', // Purple
  'from-pink-100/50 to-pink-50/50', // Pink
  'from-red-100/50 to-red-50/50', // Red
  'from-orange-100/50 to-orange-50/50', // Orange
  'from-yellow-100/50 to-yellow-50/50', // Yellow
  'from-green-100/50 to-green-50/50', // Green
  'from-cyan-100/50 to-cyan-50/50', // Cyan
];

// Darker colors for color pickers and buttons with better visibility
export const UI_PICKER_COLORS = [
  'bg-blue-200',
  'bg-purple-200',
  'bg-pink-200',
  'bg-red-200',
  'bg-orange-200',
  'bg-yellow-200',
  'bg-green-200',
  'bg-cyan-200',
];

// Mapping from gradient colors to border colors
export const GRADIENT_TO_BORDER_COLOR_MAP: Record<string, string> = {
  'from-blue-100/50 to-blue-50/50': 'border-blue-500',
  'from-purple-100/50 to-purple-50/50': 'border-purple-500',
  'from-pink-100/50 to-pink-50/50': 'border-pink-500',
  'from-red-100/50 to-red-50/50': 'border-red-500',
  'from-orange-100/50 to-orange-50/50': 'border-orange-500',
  'from-yellow-100/50 to-yellow-50/50': 'border-yellow-500',
  'from-green-100/50 to-green-50/50': 'border-green-500',
  'from-cyan-100/50 to-cyan-50/50': 'border-cyan-500',
  // Direct color names for simpler referencing
  blue: 'border-blue-500',
  purple: 'border-purple-500',
  pink: 'border-pink-500',
  red: 'border-red-500',
  orange: 'border-orange-500',
  yellow: 'border-yellow-500',
  green: 'border-green-500',
  cyan: 'border-cyan-500',
  indigo: 'border-indigo-500',
  teal: 'border-teal-500',
};

// For highlighted elements (more intense colors)
export const GRADIENT_TO_HIGHLIGHT_BORDER_COLOR_MAP: Record<string, string> = {
  'from-blue-100/50 to-blue-50/50': 'border-blue-600',
  'from-purple-100/50 to-purple-50/50': 'border-purple-600',
  'from-pink-100/50 to-pink-50/50': 'border-pink-600',
  'from-red-100/50 to-red-50/50': 'border-red-600',
  'from-orange-100/50 to-orange-50/50': 'border-orange-600',
  'from-yellow-100/50 to-yellow-50/50': 'border-yellow-600',
  'from-green-100/50 to-green-50/50': 'border-green-600',
  'from-cyan-100/50 to-cyan-50/50': 'border-cyan-600',
  // Direct color names for simpler referencing
  blue: 'border-blue-600',
  purple: 'border-purple-600',
  pink: 'border-pink-600',
  red: 'border-red-600',
  orange: 'border-orange-600',
  yellow: 'border-yellow-600',
  green: 'border-green-600',
  cyan: 'border-cyan-600',
  indigo: 'border-indigo-600',
  teal: 'border-teal-600',
};

// Mapping from gradient colors to hover background colors
export const GRADIENT_TO_HOVER_COLOR_MAP: Record<string, string> = {
  'from-blue-100/50 to-blue-50/50': 'hover:bg-blue-50',
  'from-purple-100/50 to-purple-50/50': 'hover:bg-purple-50',
  'from-pink-100/50 to-pink-50/50': 'hover:bg-pink-50',
  'from-red-100/50 to-red-50/50': 'hover:bg-red-50',
  'from-orange-100/50 to-orange-50/50': 'hover:bg-orange-50',
  'from-yellow-100/50 to-yellow-50/50': 'hover:bg-yellow-50',
  'from-green-100/50 to-green-50/50': 'hover:bg-green-50',
  'from-cyan-100/50 to-cyan-50/50': 'hover:bg-cyan-50',
  // Direct color names for simpler referencing
  blue: 'hover:bg-blue-50',
  purple: 'hover:bg-purple-50',
  pink: 'hover:bg-pink-50',
  red: 'hover:bg-red-50',
  orange: 'hover:bg-orange-50',
  yellow: 'hover:bg-yellow-50',
  green: 'hover:bg-green-50',
  cyan: 'hover:bg-cyan-50',
  indigo: 'hover:bg-indigo-50',
  teal: 'hover:bg-teal-50',
};

// Mapping from gradient colors to tag colors (consistent styling for tags)
export const GRADIENT_TO_TAG_COLOR_MAP: Record<string, string> = {
  'from-blue-100/50 to-blue-50/50': 'bg-blue-100/80 text-blue-800 border border-blue-100/80',
  'from-purple-100/50 to-purple-50/50':
    'bg-purple-100/80 text-purple-800 border border-purple-100/80',
  'from-pink-100/50 to-pink-50/50': 'bg-pink-100/80 text-pink-800 border border-pink-100/80',
  'from-red-100/50 to-red-50/50': 'bg-red-100/80 text-red-800 border border-red-100/80',
  'from-orange-100/50 to-orange-50/50':
    'bg-orange-100/80 text-orange-800 border border-orange-100/80',
  'from-yellow-100/50 to-yellow-50/50':
    'bg-yellow-100/80 text-yellow-800 border border-yellow-100/80',
  'from-green-100/50 to-green-50/50': 'bg-green-100/80 text-green-800 border border-green-100/80',
  'from-cyan-100/50 to-cyan-50/50': 'bg-cyan-100/80 text-cyan-800 border border-cyan-100/80',
  // Direct color names for simpler referencing
  blue: 'bg-blue-100/80 text-blue-800 border border-blue-100/80',
  purple: 'bg-purple-100/80 text-purple-800 border border-purple-100/80',
  pink: 'bg-pink-100/80 text-pink-800 border border-pink-100/80',
  red: 'bg-red-100/80 text-red-800 border border-red-100/80',
  orange: 'bg-orange-100/80 text-orange-800 border border-orange-100/80',
  yellow: 'bg-yellow-100/80 text-yellow-800 border border-yellow-100/80',
  green: 'bg-green-100/80 text-green-800 border border-green-100/80',
  cyan: 'bg-cyan-100/80 text-cyan-800 border border-cyan-100/80',
  indigo: 'bg-indigo-100/80 text-indigo-800 border border-indigo-100/80',
  teal: 'bg-teal-100/80 text-teal-800 border border-teal-100/80',
};

// For backward compatibility with existing code
// These aliases keep the same names used in taskColumnStore
export const COLUMN_COLORS = UI_GRADIENT_COLORS;
export const COLUMN_PICKER_COLORS = UI_PICKER_COLORS;
export const COLUMN_TO_BORDER_COLOR_MAP = GRADIENT_TO_BORDER_COLOR_MAP;
export const COLUMN_TO_HIGHLIGHT_BORDER_COLOR_MAP = GRADIENT_TO_HIGHLIGHT_BORDER_COLOR_MAP;
export const COLUMN_TO_HOVER_COLOR_MAP = GRADIENT_TO_HOVER_COLOR_MAP;
export const COLUMN_TO_TAG_COLOR_MAP = GRADIENT_TO_TAG_COLOR_MAP;